'use client';

import React, { useEffect } from 'react';
import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { WishlistDocument } from '@packages/gql/generated/shopping/WishlistDocument';
import { PageMetaData } from '@packages/modules/src/PageMetaData/PageMetaData';
import { notFound } from 'next/navigation';
import { WishlistSkeleton } from '@packages/order/src/components/Wishlist/WishlistSkeleton';
import { Wishlist } from '@packages/order/src/components/Wishlist/Wishlist';
import { WishlistEmpty } from '@packages/order/src/components/Wishlist/WishlistEmpty.client';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { LoginDialogButton } from '@packages/modules/src/Login/LoginDialog/LoginDialogButton.client';
import { PrudsysWishlistProductsRecoSlider } from '@packages/recommendations/src/components/ProductsRecoSliders/PrudsysWishlistProductsRecoSlider/PrudsysWishlistProductsRecoSlider.client';
import { useIntl } from 'react-intl';

/* GraphQL */ `
  query Wishlist($locale: String!) {
    wishlist(locale: $locale) {
      ...WishlistFragment
      count
    }
  }
`;

const Merkzettel = () => {
  const { locale } = useI18n();
  const { formatMessage } = useIntl();
  const { loggedIn } = useLoginState();
  const [{ data, fetching, error }] = useAuthQuery({
    query: WishlistDocument,
    variables: { locale: locale ?? 'de-DE' },
    requestPolicy: 'cache-and-network',
  });
  const dispatchGTMEvent = useTracking();

  useEffect(() => {
    // Glycerin Page Tracking
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'viewPage',
      eventCategory: 'wishlist',
    });
  }, [dispatchGTMEvent]);

  if (fetching) {
    return <WishlistSkeleton />;
  }

  if (error) {
    return notFound();
  }

  if (data) {
    const isWishlistFilled = !!data?.wishlist.count;

    return (
      <div data-testid="merkzettel-page">
        {/* <PageMetaData /> needs to be first component in page render flow because of timing issues with glycerin tracking */}
        <PageMetaData page={{ type: 'wishlist', template: 'wishlist' }} />
        {isWishlistFilled && <Wishlist data={data?.wishlist} />}
        {!isWishlistFilled && (
          <div className="flex gap-6 p-[1.25rem_0.75rem_1.75rem] sm:flex-col md:p-[1.25rem_1.75rem_3rem]">
            <WishlistEmpty />
            <LoginDialogButton showLogin={!loggedIn} showBackToShop={!isWishlistFilled} />
            <PrudsysWishlistProductsRecoSlider
              pageTemplate="wishlist"
              preferTitle
              title={formatMessage({
                id: 'WishlistPage.ProductRec.Headline',
                defaultMessage: 'Empfehlungen für Sie',
              })}
              fetchOnThreshold
              productIds={[]}
              elementIndex={0}
            />
          </div>
        )}
      </div>
    );
  }

  return <WishlistSkeleton />;
};

// eslint-disable-next-line import-x/no-default-export
export default Merkzettel;
